<template>
    <vx-modal size="md" :visible="!!model" @update:visible="model = null" padding="standard">
        <template v-slot:header> {{ model.id ? 'Update' : 'New' }} Target </template>
        <form role="form" class="flex flex-col gap-4">
            <div>
                <vx-label>Name</vx-label>
                <vx-input
                    type="text"
                    size="lg"
                    name="name"
                    data-test="target-name"
                    v-model="model.name"
                    :error="displayFirstError(validate.name)"
                />
            </div>

            <div>
                <vx-label>Teams</vx-label>
                <vx-selectable
                    v-model="model.teams"
                    :options="targetTeams"
                    @search="searchTeams"
                    multiple
                    label="name"
                    size="lg"
                    :closeOnSelect="false"
                    :error="displayFirstError(validate.teams)"
                    data-test="teams"
                ></vx-selectable>
            </div>
        </form>
        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click.prevent="model = null"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    :loading="submitting"
                    data-test="submit-button"
                    @click.prevent="submit"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    {{ model.id ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxButton, VxLabel, VxModal, VxSelectable, VxInput } from '@voxie/frontend-components';
import { nextTick, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { displayFirstError, validateRules } from '~/utils/validation';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminCloningClient, adminCloningTargetsClient } from '~/services';
import debounce from '~/utils/debounce';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();

const model = defineModel({
    type: Object,
    required: false,
});

const { $externalResults, catchError } = useFormRequestHandler();

const submitting = ref(false);
const emit = defineEmits(['saved']);

watch(
    () => !!model.value,
    async (showModal) => {
        validate.value.$reset();
        await nextTick();

        if (showModal) {
            searchTeams();
        }
    }
);

const validate = useVuelidate(
    {
        name: {
            required,
            maxLength: maxLength(80),
        },
        teams: {
            required,
            maxLength: maxLength(500),
        },
    },
    model,
    { $externalResults }
);

const submit = async () => {
    const isValid = await validateRules(validate);

    if (submitting.value || !isValid) {
        return;
    }

    submitting.value = true;

    const data = {
        name: model.value.name,
        teams: model.value.teams.map((team) => team.id),
    };

    try {
        const request = model.value.id
            ? await adminCloningTargetsClient.updateCloningTarget(model.value.id, data)
            : await adminCloningTargetsClient.createCloningTarget(data);

        emit('saved', request.data);
        toasted.success('Cloning Target created');
        model.value = null;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const loadingTeams = ref(false);
const targetTeams = ref([]);
const searchTeams = debounce((search) => {
    loadingTeams.value = true;
    adminCloningClient
        .getTeams({
            query: search,
            sort_field: 'id',
            sort_dir: 'desc',
            per_page: 100,
        })
        .then((response) => {
            targetTeams.value = response.data.data.map((team) => ({
                ...team,
                name: `${team.id} - ${team.name}`,
            }));
        })
        .finally(() => (loadingTeams.value = false));
}, 350);
</script>
